import React, { useCallback, useMemo, useState } from 'react';
import { microwave } from '@round/api';
import useUrlState from 'Hooks/useUrlState';
import { OrderByParam } from 'Hooks/useReactTableSortToOrderBy';
import { convertOrderByToOrdering, convertOrderingToOrderBy } from 'ui/PaginationTable/PaginationTable.helpers';
import useTiktokPostInvites, { TiktokPostInviteData } from './useTiktokPostInvites';
import styles from './Contacted.module.css';
import { Modal, Skeleton } from '@round/ui-kit';
import EmailHistory from './EmailHistory/EmailHistory';
import PostedTable from './PostedTable/PostedTable';

type Props = {
    microwaveCampaignId?: number;
};

type UrlState = {
    page: number;
    page_size: number;
} & Partial<{ ordering: microwave.GetInvitesParams['ordering'] }>;

const initialUrlState: UrlState = {
    page: 1,
    page_size: 10,
    ordering: '-post_create_time',
};

const Posted = ({ microwaveCampaignId }: Props) => {
    const [urlState, setUrlState] = useUrlState<UrlState>(initialUrlState);

    const [isEmailHistoryModalOpen, setIsEmailHistoryModalOpen] = useState(false);
    const [emailHistoryPostInvite, setEmailHistoryPostInvite] = useState<TiktokPostInviteData | null>(null);

    const page = urlState.page ? Number(urlState.page) : initialUrlState.page;
    const pageSize = urlState.page_size ? Number(urlState.page_size) : initialUrlState.page_size;
    const ordering = useMemo(
        () =>
            urlState.ordering && microwave.isGetInvitesOrderingValues(urlState.ordering)
                ? urlState.ordering
                : undefined,
        [urlState.ordering]
    );

    const setOrdering = useCallback(
        (orderBy: OrderByParam<TiktokPostInviteData>) => {
            setUrlState({ ordering: convertOrderByToOrdering(orderBy) as microwave.GetInvitesParams['ordering'] });
        },
        [setUrlState]
    );

    const { creators, loading, count, errorLoading, isInitialized, reset } = useTiktokPostInvites(
        {
            campaignId: microwaveCampaignId,
            page: page,
            pageSize,
            ordering,
            isPosted: true,
        },
        { isDisabled: !microwaveCampaignId }
    );

    const [rowsDisplayed, setRowsDisplayed] = useState(0);
    const rowsDisplayedText = `displaying ${rowsDisplayed} out of ${count}`;
    const isTableLoading = loading || (!errorLoading && !isInitialized);

    return (
        <div className={styles.container}>
            <p className={styles.rowsDisplayedText}>{isTableLoading ? <Skeleton width="7rem" /> : rowsDisplayedText}</p>

            <PostedTable
                isLoading={isTableLoading}
                data={creators}
                count={count}
                page={page}
                setPage={(page) => {
                    setUrlState({ page });
                    reset();
                }}
                orderBy={convertOrderingToOrderBy(ordering)}
                disableSortBy={false}
                onOrderByChange={(ordering) => {
                    setOrdering(ordering);
                    reset();
                }}
                manualSortBy
                pageSize={pageSize}
                setPageSize={(pageSize) => {
                    setUrlState({ page_size: pageSize ?? initialUrlState.page_size });
                    reset();
                }}
                noDataLabel={errorLoading ? "Sorry, we're experiencing technical issues" : 'No data found'}
                onPaginationChange={(state) => setRowsDisplayed(state.pageRows.length)}
                onEmailHistoryClicked={(row) => {
                    setEmailHistoryPostInvite(row);
                    setIsEmailHistoryModalOpen(true);
                }}
            />

            {emailHistoryPostInvite && (
                <Modal
                    closeOnOverlayClick
                    className={styles.emailHistoryModal}
                    isOpen={isEmailHistoryModalOpen}
                    onClose={() => {
                        setIsEmailHistoryModalOpen(false);
                        setEmailHistoryPostInvite(null);
                    }}
                >
                    <EmailHistory contactedCreator={emailHistoryPostInvite} />
                </Modal>
            )}
        </div>
    );
};

export default Posted;
